"use client"

import Image from "next/image"
import { useEffect, useRef } from "react"
import { ScrambleTextOnHover } from "@/components/scramble-text"
import { SplitFlapText, SplitFlapMuteToggle, SplitFlapAudioProvider } from "@/components/split-flap-text"
import { AnimatedNoise } from "@/components/animated-noise"
import { BitmapChevron } from "@/components/bitmap-chevron"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"

gsap.registerPlugin(ScrollTrigger)

export function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!sectionRef.current || !contentRef.current) return

    const ctx = gsap.context(() => {
      gsap.to(contentRef.current, {
        y: -100,
        opacity: 0,
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      })
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} id="hero" className="relative min-h-screen flex items-center pl-6 md:pl-28 pr-6 md:pr-12">
      <AnimatedNoise opacity={0.03} />

      {/* Left vertical logo */}
      <div className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2">
        <Image
          src="/chillarops-logo-nobg.webp"
          alt="ChillarOps — Cybersecurity Engineering"
          width={80}
          height={80}
          sizes="80px"
          className="h-16 w-auto -rotate-90 origin-left object-contain md:h-20"
          priority
        />
      </div>

      {/* Main content */}
      <div ref={contentRef} className="flex-1 w-full">
        <SplitFlapAudioProvider>
          <div className="relative flex flex-wrap items-baseline gap-x-4">
            <SplitFlapText text="Chillar" speed={80} />
            <span className="font-[var(--font-bebas)] text-5xl md:text-7xl text-accent leading-none mt-2 md:mt-0 block md:inline">
              Ops
            </span>
            <div className="w-full mt-4">
              <SplitFlapMuteToggle />
            </div>
          </div>
        </SplitFlapAudioProvider>

        <h2 className="font-[var(--font-bebas)] text-muted-foreground/60 text-[clamp(1rem,3vw,2rem)] mt-4 tracking-wide">
          Offensive Security, Defensive Infrastructure
        </h2>

        <p className="mt-12 max-w-md font-mono text-sm text-muted-foreground leading-relaxed">
          Protect your business with enterprise-grade cybersecurity, DevSecOps, cloud engineering, networking, and secure software development.
        </p>

        <div className="mt-16 flex items-center gap-8">
          <a
            href="#work"
            className="group inline-flex items-center gap-3 border border-foreground/20 px-6 py-3 font-mono text-xs uppercase tracking-widest text-foreground hover:border-accent hover:text-accent transition-all duration-200 min-h-[44px] min-w-[44px] items-center"
            aria-label="Request a security assessment - view our solutions"
          >
            <ScrambleTextOnHover text="Request Security Assessment" as="span" duration={0.6} />
            <BitmapChevron className="transition-transform duration-[400ms] ease-in-out group-hover:rotate-45" />
          </a>
          <a
            href="#signals"
            className="font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors duration-200 py-3 min-h-[44px] inline-flex items-center"
            aria-label="Explore security research and updates"
          >
            Explore Services
          </a>
        </div>
      </div>

      {/* Floating info tag */}
      <div className="absolute bottom-8 right-8 md:bottom-12 md:right-12">
        <div className="border border-border px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          v.01 / Beta Build
        </div>
      </div>
    </section>
  )
}
